import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateOneOnOneApiRequest,
  CreateOneOnOneApiResponse,
  UpdateOneOnOneApiRequest,
  UpdateOneOnOneApiResponse,
  GetEmployeeOneOnOnesApiRequest,
  GetManagerOneOnOnesApiRequest,
  GetProfessionalMeetingsApiRequest,
} from '@momentum/types';
import { OneOnOneStage } from '@momentum/types';
import { oneOnOneService } from '../services/one-on-one.service';

// Query keys factory
export const oneOnOneKeys = {
  all: ['one-on-one'] as const,
  lists: () => [...oneOnOneKeys.all, 'list'] as const,
  listByEmployee: (employeeId: string, status?: string) =>
    [...oneOnOneKeys.all, 'list', 'employee', employeeId, status] as const,
  listByManager: (managerId: string, status?: string) =>
    [...oneOnOneKeys.all, 'list', 'manager', managerId, status] as const,
  detail: (id: string) => [...oneOnOneKeys.all, 'detail', id] as const,
  managerDirectEmployeesWithMeetings: (managerId: string) =>
    [
      ...oneOnOneKeys.all,
      'managerDirectEmployeesWithMeetings',
      managerId,
    ] as const,
  professionalMeetingsAsManager: (employeeId: string) =>
    [...oneOnOneKeys.all, 'professionalMeetingsAsManager', employeeId] as const,
  professionalMeetingsAsEmployee: (employeeId: string) =>
    [
      ...oneOnOneKeys.all,
      'professionalMeetingsAsEmployee',
      employeeId,
    ] as const,
};

// Create one-on-one mutation
export const useCreateOneOnOne = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOneOnOneApiRequest) =>
      oneOnOneService.create(data),
    onSuccess: (newOneOnOne: CreateOneOnOneApiResponse) => {
      queryClient.invalidateQueries({ queryKey: oneOnOneKeys.lists() });

      queryClient.setQueryData(
        oneOnOneKeys.detail(newOneOnOne._id),
        newOneOnOne
      );
    },
    onError: (error) => {
      console.error('Failed to create one-on-one:', error);
    },
  });
};

// Update one-on-one mutation
export const useUpdateOneOnOne = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Omit<UpdateOneOnOneApiRequest, '_id'>;
    }) => oneOnOneService.update(id, data),
    onSuccess: (updatedOneOnOne: UpdateOneOnOneApiResponse) => {
      // Invalidate and refetch one-on-one lists
      queryClient.invalidateQueries({ queryKey: oneOnOneKeys.lists() });

      // Update the one-on-one data in the cache
      queryClient.setQueryData(
        oneOnOneKeys.detail(updatedOneOnOne._id),
        updatedOneOnOne
      );
    },
    onError: (error) => {
      console.error('Failed to update one-on-one:', error);
    },
  });
};

// Get one-on-ones by employee ID
export const useGetEmployeeOneOnOnes = (
  params: GetEmployeeOneOnOnesApiRequest,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: oneOnOneKeys.listByEmployee(params.employeeId, params.status),
    queryFn: () => oneOnOneService.getByEmployeeId(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled ?? true,
  });
};

// Get one-on-ones by manager ID
export const useGetManagerOneOnOnes = (
  params: GetManagerOneOnOnesApiRequest,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: oneOnOneKeys.listByManager(params.managerId, params.status),
    queryFn: () => oneOnOneService.getByManagerId(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled ?? true,
  });
};

// Get one-on-one by ID
export const useGetOneOnOneById = (
  id: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: oneOnOneKeys.detail(id),
    queryFn: () => oneOnOneService.getById(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled ?? !!id,
  });
};

// Complete stage mutation
export const useCompleteStage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      oneOnOneId,
      stage,
    }: {
      oneOnOneId: string;
      stage: OneOnOneStage;
    }) => oneOnOneService.completeStage(oneOnOneId, stage),
    onSuccess: (updatedOneOnOne) => {
      // Invalidate and refetch one-on-one lists
      queryClient.invalidateQueries({ queryKey: oneOnOneKeys.lists() });

      // Update the one-on-one data in the cache
      queryClient.setQueryData(
        oneOnOneKeys.detail(updatedOneOnOne._id),
        updatedOneOnOne
      );
    },
  });
};

// Get manager direct employees with their one-on-one meetings
export const useManagerDirectEmployeesWithMeetings = (managerId: string) => {
  return useQuery({
    queryKey: oneOnOneKeys.managerDirectEmployeesWithMeetings(managerId),
    queryFn: () =>
      oneOnOneService.getManagerDirectEmployeesWithMeetings(managerId),
    enabled: !!managerId,
    staleTime: 5 * 60 * 1000, // 5 minutes (shorter because meeting data changes)
  });
};

// Get professional meetings where user is the facilitator
export const useGetProfessionalMeetingsAsManager = (
  params: GetProfessionalMeetingsApiRequest,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: oneOnOneKeys.professionalMeetingsAsManager(params.employeeId),
    queryFn: () => oneOnOneService.getProfessionalMeetingsAsManager(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled ?? !!params.employeeId,
  });
};

// Get professional meetings where user is the employee
export const useGetProfessionalMeetingsAsEmployee = (
  params: GetProfessionalMeetingsApiRequest,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: oneOnOneKeys.professionalMeetingsAsEmployee(params.employeeId),
    queryFn: () => oneOnOneService.getProfessionalMeetingsAsEmployee(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled ?? !!params.employeeId,
  });
};

// Replace open one-on-one mutation
export const useReplaceOpenOneOnOne = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOneOnOneApiRequest) =>
      oneOnOneService.replaceOpen(data),
    onSuccess: (newOneOnOne: CreateOneOnOneApiResponse) => {
      queryClient.invalidateQueries({ queryKey: oneOnOneKeys.lists() });

      queryClient.setQueryData(
        oneOnOneKeys.detail(newOneOnOne._id),
        newOneOnOne
      );
    },
    onError: (error) => {
      console.error('Failed to replace open one-on-one:', error);
    },
  });
};
